import React, { useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Upload,
  Check,
  FileText,
  X,
  ChevronLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import SuccessModal from "../../../../../shared/components/successModal";
import { CLOUD_UPLOAD } from "../../../../../assets";

// Types
type PartyType =
  | "Plaintiff"
  | "Applicant"
  | "Petitioner"
  | "Defendant"
  | "Respondent";

interface Party {
  id: string;
  roleType: string; // Specific role selected (e.g. Plaintiff vs Applicant)
  fullName: string;
  email: string;
  address: string;
  phone: string;
  whatsapp: string;
  landmark?: string; // Specific to Defendant in screenshots
}

interface Counsel {
  fullName: string;
  email: string;
  address: string;
  phone: string;
  whatsapp: string;
  firmName: string;
}

interface CaseFormData {
  title: string;
  subjectMatter: string;
  plaintiffs: Party[];
  defendants: Party[];
  counsel: Counsel;
  documents: { id: string; file: File | null }[];
}

const CreateCase = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<CaseFormData>({
    title: "",
    subjectMatter: "",
    plaintiffs: [
      {
        id: "1",
        roleType: "Plaintiff",
        fullName: "",
        email: "",
        address: "",
        phone: "",
        whatsapp: "",
      },
    ],
    defendants: [
      {
        id: "1",
        roleType: "Defendant",
        fullName: "",
        email: "",
        address: "",
        phone: "",
        whatsapp: "",
        landmark: "",
      },
    ],
    counsel: {
      fullName: "",
      email: "",
      address: "",
      phone: "",
      whatsapp: "",
      firmName: "",
    },
    documents: [{ id: "1", file: null }],
  });

  // Helper to update form data
  const updateFormData = (updates: Partial<CaseFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const updatePlaintiff = (
    index: number,
    field: keyof Party,
    value: string,
  ) => {
    const newPlaintiffs = [...formData.plaintiffs];
    newPlaintiffs[index] = { ...newPlaintiffs[index], [field]: value };
    updateFormData({ plaintiffs: newPlaintiffs });
  };

  const updateDefendant = (
    index: number,
    field: keyof Party,
    value: string,
  ) => {
    const newDefendants = [...formData.defendants];
    newDefendants[index] = { ...newDefendants[index], [field]: value };
    updateFormData({ defendants: newDefendants });
  };

  const updateCounsel = (field: keyof Counsel, value: string) => {
    updateFormData({ counsel: { ...formData.counsel, [field]: value } });
  };

  const addPlaintiff = () => {
    updateFormData({
      plaintiffs: [
        ...formData.plaintiffs,
        {
          id: Date.now().toString(),
          roleType: "Plaintiff",
          fullName: "",
          email: "",
          address: "",
          phone: "",
          whatsapp: "",
        },
      ],
    });
  };

  const removePlaintiff = (index: number) => {
    if (formData.plaintiffs.length > 1) {
      const newPlaintiffs = [...formData.plaintiffs];
      newPlaintiffs.splice(index, 1);
      updateFormData({ plaintiffs: newPlaintiffs });
    }
  };

  const addDefendant = () => {
    updateFormData({
      defendants: [
        ...formData.defendants,
        {
          id: Date.now().toString(),
          roleType: "Defendant",
          fullName: "",
          email: "",
          address: "",
          phone: "",
          whatsapp: "",
          landmark: "",
        },
      ],
    });
  };

  const removeDefendant = (index: number) => {
    if (formData.defendants.length > 1) {
      const newDefendants = [...formData.defendants];
      newDefendants.splice(index, 1);
      updateFormData({ defendants: newDefendants });
    }
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const newDocuments = formData.documents.map((doc) =>
        doc.id === id ? { ...doc, file } : doc,
      );
      updateFormData({ documents: newDocuments });
    }
  };

  const addDocumentSlot = () => {
    updateFormData({
      documents: [
        ...formData.documents,
        { id: Date.now().toString(), file: null },
      ],
    });
  };

  const removeDocumentSlot = (index: number) => {
    if (formData.documents.length > 1) {
      const newDocuments = [...formData.documents];
      newDocuments.splice(index, 1);
      updateFormData({ documents: newDocuments });
    }
  };

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Render Steps
  const renderStepIndicator = () => {
    const steps = [
      { id: 1, label: "Description of parties" },
      { id: 2, label: "Details of counsel" },
      { id: 3, label: "Upload documents" },
      { id: 4, label: "Preview" },
    ];

    return (
      <div className="flex items-center justify-center gap-x-4 mb-12">
        {steps.map((step) => (
          <div key={step.id} className="flex flex-col items-center gap-y-2">
            <div
              className={`w-[97px] h-[12px] rounded-full ${
                currentStep >= step.id ? "bg-[#BDE0CE]" : "bg-[#E9EAEB]"
              }`}
            />
            <span
              className={`text-xs ${
                currentStep >= step.id
                  ? "text-[#1A1A1A] font-medium"
                  : "text-[#9A9A9A]"
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // --- Step 1: Description of Parties ---
  const renderStep1 = () => (
    <div className="flex flex-col gap-y-8 w-[575px] mx-auto">
      {/* Case Info */}
      <div className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6">
        <div>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => updateFormData({ title: e.target.value })}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            placeholder="Title of case folder (Name of parties)"
          />
        </div>
        <div className="relative">
          <select
            value={formData.subjectMatter}
            onChange={(e) => updateFormData({ subjectMatter: e.target.value })}
            className={`w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm appearance-none bg-white ${
              formData.subjectMatter ? "text-[#1A1A1A]" : "text-[#1A1A1A]"
            }`}
          >
            <option value="" disabled hidden>
              Subject matter
            </option>
            <option value="Criminal">Criminal</option>
            <option value="Civil">Civil</option>
            <option value="Family">Family</option>
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A1A1A] pointer-events-none" />
        </div>
      </div>

      <h2 className="text-xl font-bold text-[#1A1A1A]">
        Description of parties
      </h2>

      {/* Plaintiffs */}
      {formData.plaintiffs.map((party, index) => (
        <div
          key={party.id}
          className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6 relative"
        >
          {formData.plaintiffs.length > 1 && (
            <button
              onClick={() => removePlaintiff(index)}
              className="absolute top-4 right-4 text-red-500 hover:text-red-700"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <div className="relative w-full border-b border-[#E9EAEB] pb-2">
            <select
              value={party.roleType}
              onChange={(e) =>
                updatePlaintiff(index, "roleType", e.target.value)
              }
              className="w-full font-bold text-lg text-[#1A1A1A] bg-transparent focus:outline-none appearance-none cursor-pointer pr-8"
            >
              <option value="Plaintiff">Plaintiff</option>
              <option value="Applicant">Applicant</option>
              <option value="Petitioner">Petitioner</option>
            </select>
            <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A1A1A] pointer-events-none" />
          </div>

          <div className="flex flex-col gap-y-4">
            <div>
              <input
                type="text"
                placeholder="Full name"
                value={party.fullName}
                onChange={(e) =>
                  updatePlaintiff(index, "fullName", e.target.value)
                }
                className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[#FF3B30] text-[10px] italic">
                  Required
                </span>
              </div>
            </div>

            <input
              type="email"
              placeholder="Email address"
              value={party.email}
              onChange={(e) => updatePlaintiff(index, "email", e.target.value)}
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />

            <div>
              <input
                type="text"
                placeholder="Address"
                value={party.address}
                onChange={(e) =>
                  updatePlaintiff(index, "address", e.target.value)
                }
                className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[#FF3B30] text-[10px] italic">
                  Required
                </span>
              </div>
            </div>

            <input
              type="text"
              placeholder="Phone number"
              value={party.phone}
              onChange={(e) => updatePlaintiff(index, "phone", e.target.value)}
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />

            <input
              type="text"
              placeholder="Whatsapp number"
              value={party.whatsapp}
              onChange={(e) =>
                updatePlaintiff(index, "whatsapp", e.target.value)
              }
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={addPlaintiff}
              className="flex items-center gap-x-2 text-[#1A1A1A] font-bold text-sm hover:opacity-80"
            >
              <span>Add Claimant</span>
              <div className="w-6 h-6 rounded-full bg-[#00B074] flex items-center justify-center text-white">
                <Plus className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      ))}

      {/* Defendants */}
      {formData.defendants.map((party, index) => (
        <div
          key={party.id}
          className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6 relative"
        >
          {formData.defendants.length > 1 && (
            <button
              onClick={() => removeDefendant(index)}
              className="absolute top-4 right-4 text-red-500 hover:text-red-700"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <div className="relative w-full border-b border-[#E9EAEB] pb-2">
            <div className="w-full font-bold text-lg text-[#1A1A1A] flex items-center justify-between">
              <span>Defendant</span>
              <ChevronDown className="w-5 h-5 text-[#1A1A1A]" />
            </div>
          </div>

          <div className="flex flex-col gap-y-4">
            <div>
              <input
                type="text"
                placeholder="Full name"
                value={party.fullName}
                onChange={(e) =>
                  updateDefendant(index, "fullName", e.target.value)
                }
                className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[#FF3B30] text-[10px] italic">
                  Required
                </span>
              </div>
            </div>

            <input
              type="email"
              placeholder="Email address"
              value={party.email}
              onChange={(e) => updateDefendant(index, "email", e.target.value)}
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />

            <div>
              <input
                type="text"
                placeholder="Address"
                value={party.address}
                onChange={(e) =>
                  updateDefendant(index, "address", e.target.value)
                }
                className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[#FF3B30] text-[10px] italic">
                  Required
                </span>
              </div>
            </div>

            <input
              type="text"
              placeholder="Landmark"
              value={party.landmark}
              onChange={(e) =>
                updateDefendant(index, "landmark", e.target.value)
              }
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />

            <input
              type="text"
              placeholder="Phone number"
              value={party.phone}
              onChange={(e) => updateDefendant(index, "phone", e.target.value)}
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />

            <input
              type="text"
              placeholder="Whatsapp number"
              value={party.whatsapp}
              onChange={(e) =>
                updateDefendant(index, "whatsapp", e.target.value)
              }
              className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={addDefendant}
              className="flex items-center gap-x-2 text-[#1A1A1A] font-bold text-sm hover:opacity-80"
            >
              <span>Add Defendant</span>
              <div className="w-6 h-6 rounded-full bg-[#00B074] flex items-center justify-center text-white">
                <Plus className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={nextStep}
        className="w-full bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors mt-8"
      >
        Next
      </button>
    </div>
  );

  // --- Step 2: Details of Counsel ---
  const renderStep2 = () => (
    <div className="flex flex-col gap-y-8 w-[575px] mx-auto">
      <div className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Details of Counsel</h2>
        <h3 className="font-bold text-[#1A1A1A]">plaintiff Counsel</h3>

        <div className="flex flex-col gap-y-4">
          <input
            type="text"
            placeholder="Full name"
            value={formData.counsel.fullName}
            onChange={(e) => updateCounsel("fullName", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="email"
            placeholder="Email address"
            value={formData.counsel.email}
            onChange={(e) => updateCounsel("email", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Address"
            value={formData.counsel.address}
            onChange={(e) => updateCounsel("address", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Phone number"
            value={formData.counsel.phone}
            onChange={(e) => updateCounsel("phone", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Whatsapp number"
            value={formData.counsel.whatsapp}
            onChange={(e) => updateCounsel("whatsapp", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Name of firm"
            value={formData.counsel.firmName}
            onChange={(e) => updateCounsel("firmName", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />
        </div>
      </div>

      <button
        onClick={nextStep}
        className="w-full bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors mt-8"
      >
        Next
      </button>
    </div>
  );

  // --- Step 3: Upload Documents ---
  const renderStep3 = () => (
    <div className="flex flex-col gap-y-8 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold text-[#1A1A1A]">
        Upload case documents
      </h2>

      <div className="bg-white p-8 rounded-xl border border-[#E9EAEB] shadow-sm flex flex-col items-center justify-center gap-y-4">
        <h3 className="self-start font-bold text-[#1A1A1A] mb-2">
          Upload documents
        </h3>

        {formData.documents.map((doc, idx) => (
          <div key={doc.id} className="w-full relative">
            <input
              type="file"
              id={`file-upload-${doc.id}`}
              accept="application/pdf"
              onChange={(e) => handleFileUpload(e, doc.id)}
              className="hidden"
            />
            {doc.file ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-[#E9EAEB]">
                <div className="flex items-center gap-x-3">
                  <FileText className="w-6 h-6 text-[#00B074]" />
                  <span className="text-sm font-medium text-[#1A1A1A] truncate max-w-[200px]">
                    {doc.file.name}
                  </span>
                </div>
                <div className="flex items-center gap-x-3">
                  <button
                    onClick={() => {
                      if (doc.file) {
                        const url = URL.createObjectURL(doc.file);
                        window.open(url, "_blank");
                      }
                    }}
                    className="text-sm font-medium text-[#00B074] hover:underline"
                  >
                    View
                  </button>
                  <label
                    htmlFor={`file-upload-${doc.id}`}
                    className="text-sm font-medium text-[#1A1A1A] cursor-pointer hover:underline"
                  >
                    Change
                  </label>
                  <button
                    onClick={() => {
                      if (formData.documents.length > 1) {
                        removeDocumentSlot(idx);
                      } else {
                        const newDocs = [...formData.documents];
                        newDocs[idx].file = null;
                        updateFormData({ documents: newDocs });
                      }
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full border-2 border-dashed border-[#E9EAEB] rounded-xl p-8 flex flex-col items-center justify-center gap-y-2 bg-white hover:bg-gray-50 transition-colors cursor-pointer relative">
                <label
                  htmlFor={`file-upload-${doc.id}`}
                  className="absolute inset-0 w-full h-full cursor-pointer"
                />
                <img
                  src={CLOUD_UPLOAD}
                  alt="cloud-upload"
                  className="w-10 h-10"
                />
                <div className="text-center">
                  <span className="text-[#00B074] font-bold">
                    Click to upload
                  </span>
                  <span className="text-[#475467]"> Example case.pdf</span>
                </div>
                <span className="text-[#475467] text-xs">
                  PDF only (max. 5 MB)
                </span>
                {formData.documents.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeDocumentSlot(idx);
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700 z-10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        <div className="self-end mt-2">
          <div
            onClick={addDocumentSlot}
            className="w-8 h-8 rounded-full bg-[#00B074] flex items-center justify-center text-white cursor-pointer hover:bg-[#009b65]"
          >
            <Plus className="w-5 h-5" />
          </div>
        </div>

        <button
          onClick={nextStep}
          className="w-full bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors mt-8"
        >
          Next
        </button>
      </div>
    </div>
  );

  // --- Step 4: Preview ---
  const renderStep4 = () => (
    <div className="flex flex-col gap-y-8 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold text-[#1A1A1A]">Preview information</h2>

      <div className="bg-white p-6 rounded-xl border border-[#E9EAEB] shadow-sm flex flex-col gap-y-8">
        {/* Case Info */}
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-2">
            <label className="text-sm font-medium text-[#9A9A9A]">
              Case title
            </label>
            <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
              {formData.title || "Not specified"}
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <label className="text-sm font-medium text-[#9A9A9A]">
              Subject matter division
            </label>
            <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
              {formData.subjectMatter
                ? `${formData.subjectMatter} Court`
                : "Not specified"}
            </div>
          </div>
        </div>

        {/* Counsel Details */}
        <div>
          <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">
            Details of Counsel
          </h3>
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Full name
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.fullName || "Not specified"}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Email address
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.email || "Not specified"}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Address
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.address || "Not specified"}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Phone number
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.phone || "Not specified"}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Whatsapp number
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.whatsapp || "Not specified"}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <label className="text-sm font-medium text-[#9A9A9A]">
                Name of firm
              </label>
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                {formData.counsel.firmName || "Not specified"}
              </div>
            </div>
          </div>
        </div>

        {/* Parties */}
        <div>
          <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">
            Description of parties
          </h3>

          {formData.plaintiffs.map((party, i) => (
            <div key={i} className="flex flex-col gap-y-4 mb-6">
              <h4 className="font-bold text-[#1A1A1A]">{party.roleType}</h4>
              <div className="flex flex-col gap-y-2">
                <label className="text-sm font-medium text-[#9A9A9A]">
                  Full name
                </label>
                <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                  {party.fullName || "Not specified"}
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <label className="text-sm font-medium text-[#9A9A9A]">
                  Email address
                </label>
                <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                  {party.email || "Not specified"}
                </div>
              </div>
            </div>
          ))}

          {formData.defendants.map((party, i) => (
            <div key={i} className="flex flex-col gap-y-4 mb-6">
              <h4 className="font-bold text-[#1A1A1A]">{party.roleType}</h4>
              <div className="flex flex-col gap-y-2">
                <label className="text-sm font-medium text-[#9A9A9A]">
                  Full name
                </label>
                <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#1A1A1A] bg-white">
                  {party.fullName || "Not specified"}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Documents */}
        <div>
          <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Documents</h3>
          <div className="flex flex-col gap-y-4">
            {formData.documents.filter((doc) => doc.file).length > 0 ? (
              formData.documents
                .filter((doc) => doc.file)
                .map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-[#E9EAEB]"
                  >
                    <div className="flex items-center gap-x-3">
                      <FileText className="w-6 h-6 text-[#00B074]" />
                      <span className="text-sm font-medium text-[#1A1A1A]">
                        {doc.file?.name}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (doc.file) {
                          const url = URL.createObjectURL(doc.file);
                          window.open(url, "_blank");
                        }
                      }}
                      className="text-sm font-medium text-[#00B074] hover:underline"
                    >
                      View
                    </button>
                  </div>
                ))
            ) : (
              <div className="p-4 border border-[#E9EAEB] rounded-lg text-[#9A9A9A] bg-white italic">
                No documents uploaded
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-x-4">
        <button
          onClick={prevStep}
          className="flex-1 border border-[#00B074] text-[#00B074] py-4 rounded-xl font-medium text-lg hover:bg-green-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => {
            // Handle submission logic here
            setSuccess(true);
          }}
          className="flex-1 bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors"
        >
          Submit
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto gap-y-7 py-6 items-center pb-20">
      <span className="text-[28px] font-bold">Create appeal case folder</span>

      <div className="w-full">
        {renderStepIndicator()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      <SuccessModal
        open={success}
        close={() => setSuccess(false)}
        message="Case folder created!"
      />
    </div>
  );
};

export default CreateCase;
